import { MeasurementPoint } from "../types/measurements";
import { Line } from "fabric";
import { CustomCanvas } from "../types/fabric";

// Calculate angle between three points
export const calculateAngle = (p1: MeasurementPoint, p2: MeasurementPoint, p3: MeasurementPoint) => {
  const angle1 = Math.atan2(p1.y - p2.y, p1.x - p2.x);
  const angle2 = Math.atan2(p3.y - p2.y, p3.x - p2.x);
  let angle = Math.abs((angle1 - angle2) * (180 / Math.PI));
  if (angle > 180) angle = 360 - angle;
  return Math.round(angle);
};

// Find reference line (yellow 10mm) on canvas for scale calibration
export const findReferenceLine = (canvas: CustomCanvas): Line | null => {
  const referenceLine = canvas.getObjects().find(obj => 
    obj instanceof Line && 
    (obj as any).stroke === '#ffde59'
  ) as Line | undefined;
  
  return referenceLine || null;
};

// Keep track of the last valid pixels per mm for better fallback
let lastValidPixelsPerMm: number | null = null;

// Calculate pixels per millimeter based on reference line or DPI
export const calculatePixelsPerMm = (referenceLine: Line | null, canvas: CustomCanvas | null): number => {
  if (!canvas) {
    // If no canvas, use last known value or default
    return lastValidPixelsPerMm || 96 / 25.4; // Default DPI / mm per inch
  }

  if (referenceLine) {
    // Calculate length of reference line in raw coordinates
    const rawDx = (referenceLine.x2 || 0) - (referenceLine.x1 || 0);
    const rawDy = (referenceLine.y2 || 0) - (referenceLine.y1 || 0);
    const rawLength = Math.sqrt(rawDx * rawDx + rawDy * rawDy);

    // Convert to screen coordinates by factoring in zoom
    const zoom = canvas.getZoom();
    const referencePixels = rawLength * zoom;
    
    // Reference line represents 10mm
    const pixelsPerMm = referencePixels / 10;
    
    // Store this valid calculation for future fallbacks
    lastValidPixelsPerMm = pixelsPerMm;
    
    return pixelsPerMm;
  }

  // No reference line found
  // Check for objects on canvas to determine scale
  const objects = canvas.getObjects();
  const hasDrawings = objects.length > 0;

  // If we've ever calculated a valid scale, use that instead of DPI
  if (lastValidPixelsPerMm && hasDrawings) {
    return lastValidPixelsPerMm;
  }
  
  // Fallback to DPI-based calculation
  const dpi = 96; // Standard screen DPI
  const pixelsPerMm = dpi / 25.4; // Convert DPI to pixels per mm
  
  return pixelsPerMm;
};

// Convert pixel measurements to millimeters
export const pixelsToMillimeters = (pixels: number, canvas: CustomCanvas | null, scale: number = 1): number => {
  if (!canvas) return pixels;
  
  const referenceLine = findReferenceLine(canvas);
  const pixelsPerMm = calculatePixelsPerMm(referenceLine, canvas);
  
  // Convert raw canvas pixels to millimeters
  const zoom = canvas.getZoom();
  const unscaledPixels = (pixels * scale) / zoom;
  const mm = unscaledPixels / (pixelsPerMm / zoom);
  
  return mm;
};
